import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "wouter";
import {
  api,
  getAdminAutomationRules,
  createAdminAutomationRule,
  deleteAdminAutomationRule,
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  type AutomationRuleEnriched,
  type EmailTemplate,
} from "@/lib/api";
import { Trash2, Plus, Zap, Mail, ArrowLeft } from "lucide-react";

// ─── Email Templates tab ────────────────────────────────────────────────────

function EmailTemplatesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const [editTarget, setEditTarget] = useState<EmailTemplate | "new" | null>(null);
  const [form, setForm] = useState({ title: "", subject: "", body: "" });

  const { data: templates = [], isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["admin-email-templates"],
    queryFn: getEmailTemplates,
  });

  const saveMutation = useMutation({
    mutationFn: (data: { title: string; subject: string; body: string }) =>
      editTarget && editTarget !== "new"
        ? updateEmailTemplate(editTarget.id, data)
        : createEmailTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-email-templates"] });
      setEditTarget(null);
      toast({ title: editTarget === "new" ? "Template created" : "Template updated" });
    },
    onError: () => toast({ title: "Save failed", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-email-templates"] });
      toast({ title: "Template deleted" });
    },
    onError: () => toast({ title: "Delete failed", variant: "destructive" }),
  });

  function openNew() {
    setForm({ title: "", subject: "", body: "" });
    setEditTarget("new");
  }

  function openEdit(t: EmailTemplate) {
    setForm({ title: t.title, subject: t.subject, body: t.body });
    setEditTarget(t);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          HTML email templates with <code className="text-xs bg-muted px-1 rounded">{"{{placeholder}}"}</code> support.
          Available tokens: <code className="text-xs">project_title</code>, <code className="text-xs">accounting_period</code>,{" "}
          <code className="text-xs">client_name</code>, <code className="text-xs">company_name</code>,{" "}
          <code className="text-xs">task_title</code>, <code className="text-xs">due_date</code>.
        </p>
        <Button size="sm" onClick={openNew}>
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground text-sm">
          No email templates yet. Create one to use with automation rules.
        </div>
      ) : (
        <div className="divide-y border rounded-lg">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30">
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{t.title}</p>
                <p className="text-xs text-muted-foreground truncate">{t.subject}</p>
              </div>
              <div className="flex gap-2 shrink-0 ml-4">
                <Button size="sm" variant="ghost" onClick={() => openEdit(t)}>Edit</Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={async () => {
                    if (await askConfirm({
                      title: "Delete this email template?",
                      subject: "email template",
                      name: t.title,
                      body: "will be deleted.",
                      confirmLabel: "Delete",
                      destructive: true,
                    })) deleteMutation.mutate(t.id);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editTarget !== null} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editTarget === "new" ? "New Email Template" : "Edit Email Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title (internal name)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Document Request Reminder" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Documents needed for {{project_title}}" />
            </div>
            <div>
              <Label>Body (HTML)</Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={10}
                className="font-mono text-xs"
                placeholder={"<p>Hello {{client_name}},</p>\n<p>We are working on <strong>{{project_title}}</strong> and need documents.</p>"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              onClick={() => saveMutation.mutate(form)}
              disabled={!form.title || !form.subject || !form.body || saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Action type badge ──────────────────────────────────────────────────────

function ActionBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    add_tag: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    remove_tag: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    send_email: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    create_project: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  };
  const labels: Record<string, string> = {
    add_tag: "Add Tag",
    remove_tag: "Remove Tag",
    send_email: "Send Email",
    create_project: "Create Project",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[type] ?? "bg-muted text-muted-foreground"}`}>
      {labels[type] ?? type}
    </span>
  );
}

// ─── Create rule form ───────────────────────────────────────────────────────

interface CreateRuleFormProps {
  onClose: () => void;
}

function CreateRuleForm({ onClose }: CreateRuleFormProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [scopeType, setScopeType] = useState<"task" | "section">("task");
  const [scopeSearch, setScopeSearch] = useState("");
  const [selectedScopeId, setSelectedScopeId] = useState("");
  const [actionType, setActionType] = useState("add_tag");
  const [actionRefId, setActionRefId] = useState("");
  const [contacts, setContacts] = useState(""); // comma-separated for send_email

  const { data: emailTemplates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["admin-email-templates"],
    queryFn: getEmailTemplates,
  });

  // Search tasks or sections
  const { data: scopeResults = [] } = useQuery<Array<{ id: string; title?: string; name?: string; project_title?: string }>>({
    queryKey: ["scope-search", scopeType, scopeSearch],
    queryFn: async () => {
      if (scopeSearch.trim().length < 2) return [];
      const token = await (async () => {
        const { getSupabase } = await import("@/lib/supabase");
        const sb = await getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        return session?.access_token ?? "";
      })();
      const table = scopeType === "task" ? "project_tasks" : "task_sections";
      const nameCol = scopeType === "task" ? "title" : "name";
      const url = `/api/admin/scope-search?table=${table}&q=${encodeURIComponent(scopeSearch)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: scopeSearch.trim().length >= 2,
  });

  const { data: tags = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["tags-list-admin"],
    queryFn: async () => {
      const rows = await api.getTags();
      return rows.map((t) => ({ id: t.id, name: t.name }));
    },
  });

  const { data: projectTemplates = [] } = useQuery<Array<{ id: string; title: string }>>({
    queryKey: ["project-templates-admin"],
    queryFn: async () => {
      const rows = await api.getProjectTemplates();
      return rows.map((t) => ({ id: t.id, title: t.title }));
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        action_type: actionType,
        action_ref_id: actionRefId || undefined,
        action_config: actionType === "send_email" && contacts
          ? { contacts: contacts.split(",").map((s) => s.trim()).filter(Boolean) }
          : {},
      };
      if (scopeType === "task") payload.scope_task_id = selectedScopeId;
      else payload.scope_section_id = selectedScopeId;
      return createAdminAutomationRule(payload as Parameters<typeof createAdminAutomationRule>[0]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-automation-rules"] });
      toast({ title: "Automation rule created" });
      onClose();
    },
    onError: () => toast({ title: "Create failed", variant: "destructive" }),
  });

  const canSubmit = selectedScopeId && actionType &&
    (actionType === "create_project" ? !!actionRefId : true) &&
    (actionType === "add_tag" || actionType === "remove_tag" ? !!actionRefId : true);

  return (
    <div className="space-y-4">
      {/* Scope */}
      <div className="space-y-2">
        <Label>Trigger when…</Label>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={scopeType === "task" ? "default" : "outline"}
            onClick={() => { setScopeType("task"); setSelectedScopeId(""); setScopeSearch(""); }}
          >Task completed</Button>
          <Button
            size="sm"
            variant={scopeType === "section" ? "default" : "outline"}
            onClick={() => { setScopeType("section"); setSelectedScopeId(""); setScopeSearch(""); }}
          >Section completed</Button>
        </div>

        <Input
          placeholder={`Search ${scopeType === "task" ? "task" : "section"} name…`}
          value={scopeSearch}
          onChange={(e) => { setScopeSearch(e.target.value); setSelectedScopeId(""); }}
        />
        {scopeResults.length > 0 && !selectedScopeId && (
          <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
            {scopeResults.map((r) => (
              <button
                key={r.id}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50"
                onClick={() => { setSelectedScopeId(r.id); setScopeSearch(r.title ?? r.name ?? r.id); }}
              >
                <span className="font-medium">{r.title ?? r.name}</span>
                {r.project_title && <span className="text-muted-foreground ml-2 text-xs">in {r.project_title}</span>}
              </button>
            ))}
          </div>
        )}
        {selectedScopeId && (
          <p className="text-xs text-green-600">Selected: {scopeSearch}</p>
        )}
      </div>

      {/* Action */}
      <div>
        <Label>Action</Label>
        <Select value={actionType} onValueChange={(v) => { setActionType(v); setActionRefId(""); }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="add_tag">Add Tag</SelectItem>
            <SelectItem value="remove_tag">Remove Tag</SelectItem>
            <SelectItem value="send_email">Send Email</SelectItem>
            <SelectItem value="create_project">Create Project from Template</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Target */}
      {(actionType === "add_tag" || actionType === "remove_tag") && (
        <div>
          <Label>Tag</Label>
          <Select value={actionRefId} onValueChange={setActionRefId}>
            <SelectTrigger><SelectValue placeholder="Select tag…" /></SelectTrigger>
            <SelectContent>
              {tags.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {actionType === "send_email" && (
        <>
          <div>
            <Label>Email Template</Label>
            <Select value={actionRefId} onValueChange={setActionRefId}>
              <SelectTrigger><SelectValue placeholder="Select template…" /></SelectTrigger>
              <SelectContent>
                {emailTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Recipients (comma-separated emails)</Label>
            <Input
              value={contacts}
              onChange={(e) => setContacts(e.target.value)}
              placeholder="client@example.com, staff@ccpas.com"
            />
          </div>
        </>
      )}

      {actionType === "create_project" && (
        <div>
          <Label>Project Template</Label>
          <Select value={actionRefId} onValueChange={setActionRefId}>
            <SelectTrigger><SelectValue placeholder="Select template…" /></SelectTrigger>
            <SelectContent>
              {projectTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={!canSubmit || createMutation.isPending}
        >
          {createMutation.isPending ? "Creating…" : "Create Rule"}
        </Button>
      </div>
    </div>
  );
}

// ─── Automation Rules tab ───────────────────────────────────────────────────

function AutomationRulesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const [showCreate, setShowCreate] = useState(false);

  const { data: rules = [], isLoading } = useQuery<AutomationRuleEnriched[]>({
    queryKey: ["admin-automation-rules"],
    queryFn: getAdminAutomationRules,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAdminAutomationRule,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-automation-rules"] });
      toast({ title: "Rule deleted" });
    },
    onError: () => toast({ title: "Delete failed", variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Rules fire when a task or section is completed. Idempotent — each rule fires at most once per event.
          {rules.length > 0 && <span className="ml-2 font-medium text-foreground">{rules.length.toLocaleString()} rules</span>}
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1" /> New Rule
        </Button>
      </div>

      {showCreate && (
        <div className="border rounded-lg p-4 bg-muted/20">
          <h3 className="font-semibold text-sm mb-3">Create Automation Rule</h3>
          <CreateRuleForm onClose={() => setShowCreate(false)} />
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rules.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground text-sm">
          No automation rules. Rules are imported from FC or created here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">When this completes…</th>
                <th className="pb-2 pr-4 font-medium">…do this</th>
                <th className="pb-2 pr-4 font-medium">Last Fired</th>
                <th className="pb-2 pr-4 font-medium text-right">Fires</th>
                <th className="pb-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rules.map((rule) => (
                <tr key={rule.id} className="hover:bg-muted/20">
                  <td className="py-2 pr-4">
                    <p className="font-medium truncate max-w-[200px]">{rule.scopeName || <span className="text-muted-foreground italic">Unknown</span>}</p>
                    {rule.scopeProjectName && (
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{rule.scopeProjectName}</p>
                    )}
                    <Badge variant="outline" className="text-xs mt-0.5">
                      {rule.scope_task_id ? "task" : "section"}
                    </Badge>
                  </td>
                  <td className="py-2 pr-4 max-w-[240px]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <ActionBadge type={rule.action_type} />
                      {rule.targetName && (
                        <span className="text-sm font-medium truncate max-w-[160px]">"{rule.targetName}"</span>
                      )}
                    </div>
                    {rule.action_type === "send_email" && Array.isArray((rule.action_config as any)?.contacts) && (
                      <span className="text-xs text-muted-foreground">{((rule.action_config as any).contacts as string[]).join(", ")}</span>
                    )}
                    {!rule.targetName && <span className="text-xs text-muted-foreground italic">—</span>}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground text-xs">
                    {rule.lastFiredAt
                      ? new Date(rule.lastFiredAt).toLocaleDateString()
                      : "Never"}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{rule.fireCount}</td>
                  <td className="py-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={async () => {
                        if (await askConfirm({
                          title: "Delete this automation rule?",
                          subject: "automation rule",
                          body: "will be deleted and will stop firing.",
                          confirmLabel: "Delete",
                          destructive: true,
                        })) deleteMutation.mutate(rule.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AutomationRules() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  if (user && user.role !== "admin") {
    navigate("/settings");
    return null;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/settings/admin")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin Settings
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight mb-1 flex items-center gap-2">
            <Zap className="w-7 h-7 text-amber-500" /> Automation Rules
          </h1>
          <p className="text-muted-foreground">
            Configure what happens automatically when tasks or sections are completed.
          </p>
        </div>

        <Tabs defaultValue="rules">
          <TabsList>
            <TabsTrigger value="rules" className="flex items-center gap-1.5">
              <Zap className="w-4 h-4" /> Rules
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-1.5">
              <Mail className="w-4 h-4" /> Email Templates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rules" className="mt-4">
            <AutomationRulesTab />
          </TabsContent>

          <TabsContent value="templates" className="mt-4">
            <EmailTemplatesTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
